import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import useTranslation from '~/app/hooks/useTranslation';
import { notify } from '~/app/helpers/notify';
import Icon, { MaterialIcons } from '~/app/common/Icon';
import LinkWrapper from '~/app/common/LinkWrapper';
import { route } from '~/app/helpers/route';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { WORKORDER_ROUTES } from '~/workorder/Router';
import { WorkOrderDetail, WorkOrderStatuses, WorkOrderTask, WorkOrderTaskStatus } from '~/workorder/types';
import classes from './style.module.scss';
import { useStoreActions, useStoreState } from '~/store';

export type TasksComponent = React.FC<{ data: WorkOrderDetail }>;

const getNextTaskStatus = (status: WorkOrderTaskStatus): WorkOrderTaskStatus => {
    if (status === WorkOrderTaskStatus.INCOMPLETE) {
        return WorkOrderTaskStatus.COMPLETED;
    }
    if (status === WorkOrderTaskStatus.COMPLETED) {
        return WorkOrderTaskStatus.REMOVED;
    }
    return WorkOrderTaskStatus.INCOMPLETE;
};

const Tasks: TasksComponent = ({ data }) => {
    const { t } = useTranslation('workorder');
    const { t: tAPIResponseMessages } = useTranslation('apiResponseMessages');
    const [expanded, setExpanded] = useState(false);
    const totalHours = useStoreState(state => state.workOrder.data?.summary?.totalHours);
    const taskCount = useStoreState(state => state.workOrder.taskCount);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const workOrderStatus = useStoreState(state => state.workOrder.data?.status);
    const setData = useStoreActions(state => state.workOrder.setData);
    const autoSaveWorkOrderData = useStoreActions(state => state.workOrder.autoSaveWorkOrderData);
    const { total, done } = taskCount;

    const isDisabled = useMemo(() => {
        return (
            workOrderStatus === WorkOrderStatuses.APPROVED ||
            workOrderStatus === WorkOrderStatuses.DELAYED ||
            workOrderStatus === WorkOrderStatuses.ONHOLD
        );
    }, [workOrderStatus]);

    const handleTaskStatusChange = useCallback((task: WorkOrderTask) => {
        if (isDisabled || task.type === IssueOrServiceType.SERVICE) {
            return;
        }

        const previousStatus = task.status;
        const nextStatus = getNextTaskStatus(previousStatus);
        const updatedTasks = data.tasks.map(t => t.id === task.id ? { ...t, status: nextStatus } : t);
        setData({ ...data, tasks: updatedTasks });

        if (task.type !== IssueOrServiceType.ISSUE || netWorkStatus !== 0) {
            return;
        }

        autoSaveWorkOrderData({ workOrderId: data.id, issueId: task.id, key: 'issueStatus', value: nextStatus, userCount: totalHours })
            .then(response => {
                if (response.success) {
                    notify(tAPIResponseMessages('changesSaved'), 'success');
                } else {
                    const revertedTasks = data.tasks.map(t => t.id === task.id ? { ...t, status: previousStatus } : t);
                    setData({ ...data, tasks: revertedTasks });
                }
            })
            .catch(() => {
                const revertedTasks = data.tasks.map(t => t.id === task.id ? { ...t, status: previousStatus } : t);
                setData({ ...data, tasks: revertedTasks });
                notify(t('detail.error.taskStatusUpdateFailed'), 'error');
            });
    }, [autoSaveWorkOrderData, data, netWorkStatus, setData, isDisabled, t, tAPIResponseMessages, totalHours]);

    const getIconProperties = (task: WorkOrderTask) => {
        if (task.status === WorkOrderTaskStatus.INCOMPLETE) {
            return {
                name: 'radio_button_unchecked',
                className: classes['unchecked'],
            };
        } else if (task.status === WorkOrderTaskStatus.COMPLETED) {
            return {
                name: 'check_circle',
                className: classes['checked'],
            };
        } else if (task.status === WorkOrderTaskStatus.REMOVED) {
            return {
                name: 'remove_circle',
                className: classes['removed'],
            };
        }
    }

    const canAddTask = useMemo(() => {
        return !isDisabled && netWorkStatus === 0;
    }, [isDisabled, netWorkStatus]);

    return (
        <div className={classes['card']}>

            <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
                <h2 className={classes['header-title']}>{t('detail.tasks')} ({done}/{total})</h2>
                {canAddTask && (
                    <LinkWrapper
                        to={route(WORKORDER_ROUTES.addTask, { id: data.id })}
                        className={classes['add-link']}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        {t('detail.addTask')}
                    </LinkWrapper>
                )}
                <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
            </div>

            {expanded && (
                (total > 0 ?
                    <div className={classes['list']}>
                        {data.tasks.map(task => {
                            const icon = getIconProperties(task);
                            const isIssue = task.type === IssueOrServiceType.ISSUE;
                            const to = route(WORKORDER_ROUTES.taskDetail, {
                                id: data.id,
                                taskId: task.id,
                                type: isIssue ? 'issue' : 'service'
                            });

                            return (
                                <div key={task.id} className={classNames(classes['row'], { [classes['disabled']]: isDisabled })}>
                                    <Icon name={icon?.name as MaterialIcons} size={22} className={icon?.className} onClick={() => handleTaskStatusChange(task)} />
                                    <div
                                        className={classNames(
                                            classes['type-icon-wrap'],
                                            isIssue ? classes.issue : classes.service,
                                        )}
                                    >
                                        <Icon name={isIssue ? 'warning' : 'build'} />
                                    </div>
                                    <div className={classes['content']}>
                                        <p className={classNames(classes['task-title'], task.status === WorkOrderTaskStatus.INCOMPLETE ? classes.incomplete : classes.completed)}>{task.title}</p>
                                        <p className={classes['task-type']}>
                                            {t(`detail.taskType.${task.type}`)}
                                        </p>
                                    </div>
                                    <LinkWrapper to={to}>
                                        <Icon name="keyboard_arrow_right" className={classes['chevron']} />
                                    </LinkWrapper>
                                </div>
                            );
                        })}
                    </div>
                    : <p className={classes.empty}>{t('detail.noTasks')}</p>)
            )}
        </div>
    );
};

export default Tasks;
